/** @jsxImportSource @emotion/react */
'use memo'

// web/src/features/event/components/EventDetailSkeleton.tsx
import css from '@emotion/css/macro'
import { keyframes } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'

/** Detail rows in the right-hand card: date, location, arrival, team. */
const DETAIL_ROWS = 4

/**
 * Loading placeholder for the event detail page.
 *
 * Mirrors `EventDetailBody`'s real layout — same grid ratio, same gutters, same
 * card geometry — so nothing shifts when the loaded content swaps in. Follows
 * the conventions established by `RegistrationSetupSkeleton` (pulse animation,
 * shared `bar` style).
 *
 * Purely decorative, so it is hidden from assistive tech; the page's own
 * loading state is what a screen reader announces.
 */
const EventDetailSkeleton = () => (
  <div data-testid='event-detail-skeleton' aria-hidden='true'>
    {/* `Page` renders no header at all while loading (`{loading ? null : …}`,
        shared by every page), so without this the screen is an empty dark band
        under the navbar. Stands in for the icon + title + date line. */}
    <div css={headerStyle}>
      <div css={[bar, headerIconStyle]} />
      <div css={headerTextStyle}>
        <div css={[bar, headerTitleStyle]} />
        <div css={[bar, headerSubtitleStyle]} />
      </div>
    </div>

    <div css={bodyStyle}>
      {/* Left: description, then details — mirroring the loaded layout's
          most common case (an event with a location). */}
      <div css={columnStyle}>
        <section>
          <div css={sectionLabelBarStyle} />
          <div css={cardStyle}>
            <div css={[bar, lineStyle]} />
            <div css={[bar, lineStyle]} />
            <div css={[bar, lineShortStyle]} />
          </div>
        </section>

        <section>
          <div css={sectionLabelBarStyle} />
          <div css={cardStyle}>
            {Array.from({ length: DETAIL_ROWS }).map((_, row) => (
              <div key={row} css={rowStyle}>
                <div css={[bar, rowIconStyle]} />
                <div css={rowContentStyle}>
                  <div css={[bar, rowLabelStyle]} />
                  <div css={[bar, rowValueStyle]} />
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Right: the location card, map included. */}
      <div css={columnStyle}>
        <section>
          <div css={sectionLabelBarStyle} />
          <div css={cardStyle}>
            <div css={[bar, mapStyle]} />
            <div css={rowStyle}>
              <div css={[bar, rowIconStyle]} />
              <div css={rowContentStyle}>
                <div css={[bar, rowLabelStyle]} />
                <div css={[bar, rowValueStyle]} />
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
)

export default EventDetailSkeleton

// ---- styled ----

const pulse = keyframes`
  0% { opacity: 0.5; }
  50% { opacity: 0.9; }
  100% { opacity: 0.5; }
`

const bar = css`
  border-radius: 8px;
  background-color: ${colors.SECONDARY_300};
  animation: ${pulse} 1.4s ease-in-out infinite;
`

/* Header placeholder. Height and gutters approximate PageHeader's own so the
   body below does not jump when the real header appears. */
const headerStyle = css`
  display: flex;
  align-items: center;
  gap: 20px;
  max-width: 1600px;
  margin: 0 auto;
  padding: 32px 88px;

  @media (max-width: 768px) {
    gap: 12px;
    padding: 20px 16px;
  }
`

const headerIconStyle = css`
  width: 72px;
  height: 72px;
  flex-shrink: 0;
  border-radius: 12px;

  @media (max-width: 768px) {
    width: 48px;
    height: 48px;
  }
`

const headerTextStyle = css`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  flex: 1;
`

const headerTitleStyle = css`
  height: 30px;
  width: min(360px, 60%);
`

const headerSubtitleStyle = css`
  height: 14px;
  width: min(260px, 45%);
`

/* Kept in lockstep with EventDetailBody's bodyStyle — same columns, gap,
   max-width and padding — so the swap from skeleton to content is seamless. */
const bodyStyle = css`
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(340px, 1fr);
  gap: 32px;
  margin: 0 auto 40px;
  /* No top padding here (unlike EventDetailBody): the header placeholder above
     already supplies that gap, and doubling it would push the cards down. */
  padding: 0 88px;
  max-width: 1600px;
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  @media (max-width: 768px) {
    margin: 0 auto 24px;
    padding: 0 16px;
  }
`

/* Stacks cards within a column, matching EventDetailBody's columnStyle. */
const columnStyle = css`
  display: flex;
  flex-direction: column;
  gap: 32px;
  min-width: 0;

  @media (max-width: 1100px) {
    gap: 24px;
  }
`

/* Stands in for the "ABOUT THIS EVENT" / "DETAILS" headings above each card. */
const sectionLabelBarStyle = css`
  ${bar}
  height: 14px;
  width: 140px;
  margin-bottom: 12px;
`

const cardStyle = css`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  border-radius: 16px;
  background: ${colors.SECONDARY_400};
  border: 1px solid ${colors.SOFT_STEEL};
`

const lineStyle = css`
  height: 14px;
  width: 100%;
`

const lineShortStyle = css`
  height: 14px;
  width: 60%;
`

const mapStyle = css`
  height: 180px;
  width: 100%;
  border-radius: 12px;
`

const rowStyle = css`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`

const rowIconStyle = css`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  border-radius: 4px;
`

const rowContentStyle = css`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 0;
`

const rowLabelStyle = css`
  height: 12px;
  width: 90px;
`

const rowValueStyle = css`
  height: 16px;
  width: 70%;
`
